import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { X } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'

import { track } from '@/api/tracking'
import { COOKIE_DECISION_TOAST_MS } from '@/components/CookieConsent'
import { FOOD_COST_LANDING_PAGE, FORM_HASH } from '@/components/food-cost/constants'
import { useCookieConsent } from '@/hooks/useCookieConsent'
import { ROUTES } from '@/navigation/constants'

const DESKTOP_QUERY = '(min-width: 768px)'
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
const SEEN_STORAGE_KEY = 'tipi_home_router_popup_seen'
/** Wait after the cookie banner is gone so both overlays never stack. */
const SHOW_DELAY_MS = 1200

type PopupChoice = 'router' | 'stay' | 'close'

function hasSeenPopup(): boolean {
    try {
        return window.localStorage.getItem(SEEN_STORAGE_KEY) === '1'
    } catch {
        return false
    }
}

function markPopupSeen() {
    try {
        window.localStorage.setItem(SEEN_STORAGE_KEY, '1')
    } catch {
        // Private mode or blocked storage: the popup may show again next visit.
    }
}

/** Keeps ad params (UTM, fbclid) so the router can pass them on to the final landing. */
function routerSearch(): string {
    const params = new URLSearchParams(window.location.search)
    params.set('from', 'home')
    return `?${params.toString()}`
}

/**
 * Welcome popup on the Spanish home: offers the /empieza quiz or staying on the page.
 * Shown once per visitor, never with the cookie banner, and as a bottom sheet on mobile
 * so it does not cover the page like an interstitial.
 */
export function HomeRouterPopup() {
    const { t } = useTranslation()
    const { isVisible: isCookieBannerVisible, isPreferencesPanelOpen } = useCookieConsent()
    const prefersReducedMotion = useReducedMotion()
    const [isOpen, setIsOpen] = useState(false)
    const hasShown = useRef(false)
    const sawCookieBanner = useRef(false)
    const primaryRef = useRef<HTMLAnchorElement>(null)
    const dialogRef = useRef<HTMLDivElement>(null)
    const [isDesktop, setIsDesktop] = useState(false)

    useEffect(() => {
        if (isCookieBannerVisible) sawCookieBanner.current = true
        if (hasShown.current || isCookieBannerVisible || isPreferencesPanelOpen) return
        if (hasSeenPopup()) return

        // Right after a cookie decision, let its confirmation toast go away first.
        const delay = sawCookieBanner.current ? COOKIE_DECISION_TOAST_MS + SHOW_DELAY_MS / 2 : SHOW_DELAY_MS
        const timer = window.setTimeout(() => {
            // Visitors heading to the form (deep link or CTA scroll) already know what they want.
            if (window.location.hash === FORM_HASH) return
            hasShown.current = true
            markPopupSeen()
            setIsOpen(true)
            track('Home Router Popup Shown', { landing_page: FOOD_COST_LANDING_PAGE })
        }, delay)
        return () => window.clearTimeout(timer)
    }, [isCookieBannerVisible, isPreferencesPanelOpen])

    const choose = useCallback((choice: PopupChoice) => {
        track('Home Router Popup Choice', { landing_page: FOOD_COST_LANDING_PAGE, choice })
        setIsOpen(false)
    }, [])

    useEffect(() => {
        if (!isOpen) return
        const desktop = window.matchMedia(DESKTOP_QUERY).matches
        setIsDesktop(desktop)
        const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
        primaryRef.current?.focus({ preventScroll: true })
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') choose('close')
            if (event.key !== 'Tab' || !desktop || !dialogRef.current) return
            const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }
        window.addEventListener('keydown', onKeyDown)
        return () => {
            window.removeEventListener('keydown', onKeyDown)
            previouslyFocused?.focus({ preventScroll: true })
        }
    }, [isOpen, choose])

    const slide = prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 32 }

    return (
        <AnimatePresence>
            {isOpen ? (
                <div className='pointer-events-none fixed inset-0 z-50 flex items-end justify-center md:items-center md:p-6'>
                    <motion.div
                        aria-hidden
                        className='pointer-events-auto absolute inset-0 hidden bg-[#17130E]/45 md:block'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => choose('close')}
                    />
                    <motion.div
                        ref={dialogRef}
                        role='dialog'
                        aria-modal={isDesktop}
                        aria-labelledby='home-router-popup-title'
                        aria-describedby='home-router-popup-description'
                        className="pointer-events-auto relative w-full rounded-t-[26px] bg-[#F5EEE4] px-[22px] pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-6 font-['Archivo',system-ui,sans-serif] text-[#17130E] shadow-[0_-12px_40px_rgba(23,19,14,0.35)] md:max-w-[460px] md:rounded-[26px] md:px-8 md:pb-8 md:pt-8 md:shadow-[0_24px_60px_rgba(23,19,14,0.35)]"
                        initial={slide}
                        animate={{ opacity: 1, y: 0 }}
                        exit={slide}
                        transition={{ duration: 0.28, ease: 'easeOut' }}
                    >
                        <button
                            type='button'
                            onClick={() => choose('close')}
                            aria-label={t('foodCost.routerPopup.close', 'Cerrar')}
                            className='absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full text-[#5C5548] transition-colors hover:bg-[#17130E]/5'
                        >
                            <X className='h-5 w-5' aria-hidden />
                        </button>

                        <p className='m-0 text-[12px] font-bold uppercase tracking-[0.14em] text-[#D13D1A]'>
                            {t('foodCost.routerPopup.eyebrow', '3 preguntas · 30 segundos')}
                        </p>
                        <h2
                            id='home-router-popup-title'
                            className='m-0 mt-2 max-w-[22ch] pr-8 text-[24px] font-medium leading-[1.15] tracking-[-0.6px] md:text-[28px]'
                        >
                            {t('foodCost.routerPopup.title', '¿Te ayudamos a decidir qué solución es la tuya?')}
                        </h2>
                        <p
                            id='home-router-popup-description'
                            className='m-0 mt-2 text-[15px] leading-[1.55] text-[#5C5548]'
                        >
                            {t(
                                'foodCost.routerPopup.description',
                                'Herramienta, acompañamiento o asesoría: te decimos qué encaja con tu negocio.',
                            )}
                        </p>

                        <div className='mt-5 flex flex-col gap-2.5'>
                            <Link
                                ref={primaryRef}
                                to={{ pathname: ROUTES.empieza, search: routerSearch() }}
                                onClick={() => choose('router')}
                                className='flex min-h-[52px] items-center justify-center rounded-full bg-[#D13D1A] px-5 py-3 text-center text-[15px] font-bold leading-[1.2] text-white no-underline transition-opacity hover:opacity-90'
                            >
                                {t(
                                    'foodCost.routerPopup.routerCta',
                                    'Saber qué solución de Tipi encaja mejor con mi negocio',
                                )}
                            </Link>
                            <button
                                type='button'
                                onClick={() => choose('stay')}
                                className='flex min-h-[48px] items-center justify-center rounded-full border border-[#17130E]/15 px-5 py-3 text-center text-[15px] font-semibold leading-[1.2] text-[#17130E] transition-colors hover:bg-[#17130E]/5'
                            >
                                {t('foodCost.routerPopup.stayCta', 'Conocer más cómo funciona Tipi')}
                            </button>
                        </div>
                    </motion.div>
                </div>
            ) : null}
        </AnimatePresence>
    )
}
